import { defineStore } from 'pinia';
import { findClosestDomain } from '@vft/core/transform/domainMatcher.js';
import { buildDomainIndex } from '@vft/core/validation/domain.js';
import { splitMulti } from '@vft/core/validation';
import { useFileStore } from './fileStore.js';
import { useDefinitionStore } from './definitionStore.js';
import { useMappingStore } from './mappingStore.js';

function getFieldId(field) {
  return field.id || field.name || field.label;
}

function getDomain(field) {
  return field?.validation?.domain ?? field?.domain ?? field?.domainValue ?? null;
}

// Un camp és multiresposta si el seu màxim de respostes és > 1 (mateix criteri que la
// validació a core/validation/index.js). Aleshores una cel·la pot portar diversos codis
// units pel separador de multiresposta (p. ex. "U.1|U.2") i cal partir-la ABANS de
// comparar cada codi amb el domini; si no, la cadena sencera no casa mai i surt "pendent".
function fieldMandatory(field) {
  return field?.validation?.mandatory ?? field?.mandatory ?? null;
}
function isMultiField(field) {
  const m = fieldMandatory(field);
  const max = m?.max ?? field?.max;
  return max != null && max > 1;
}
function fieldSeparators(field) {
  return fieldMandatory(field)?.separators;
}

export function normalizeRawKey(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim().toLowerCase();
}

export const useValueMapStore = defineStore('valueMap', {
  state: () => ({
    // { fieldId: { normalizedRawKey: permittedKey } }
    byField: {},
  }),
  getters: {
    eligibleFields() {
      const def = useDefinitionStore();
      const mapping = useMappingStore();
      if (!def.hasDefinition) return [];
      return def.fields
        .filter((f) => {
          const dom = getDomain(f);
          if (!Array.isArray(dom) || dom.length === 0) return false;
          const fid = getFieldId(f);
          const col = mapping.mapping[fid];
          return !!col;
        })
        .map((f) => ({ field: f, fieldId: getFieldId(f) }));
    },
    rawValuesByField() {
      const file = useFileStore();
      const mapping = useMappingStore();
      const result = {};
      if (!file.hasData) return result;
      const headerIndex = new Map(file.headers.map((h, i) => [h, i]));

      for (const { field, fieldId } of this.eligibleFields) {
        const colName = mapping.mapping[fieldId];
        if (!colName || !headerIndex.has(colName)) continue;
        const colIdx = headerIndex.get(colName);
        const multi = isMultiField(field);
        const seps = fieldSeparators(field);
        const distinct = new Map();
        for (const row of file.rows) {
          const v = row[colIdx];
          if (v === null || v === undefined || v === '') continue;
          // Camp multiresposta: parteix la cel·la pel separador (p. ex. "U.1|U.2" → U.1, U.2)
          // perquè cada codi es compari individualment amb el domini. Un camp de valor únic
          // es manté sencer (no es trenca per un separador que pugui formar part del valor).
          const pieces = multi ? splitMulti(v, seps) : [v];
          for (const piece of pieces) {
            const norm = normalizeRawKey(piece);
            if (!norm) continue;
            const existing = distinct.get(norm);
            if (existing) existing.count += 1;
            else distinct.set(norm, { normalizedKey: norm, sample: piece, count: 1 });
          }
        }
        result[fieldId] = Array.from(distinct.values()).sort(
          (a, b) => b.count - a.count || String(a.sample).localeCompare(String(b.sample)),
        );
      }
      return result;
    },
    summaryByField() {
      const raws = this.rawValuesByField;
      const byField = this.byField;
      const result = {};
      for (const { field, fieldId } of this.eligibleFields) {
        const distinct = raws[fieldId] || [];
        const idx = buildDomainIndex(getDomain(field) || []);
        const assigned = byField[fieldId] || {};
        let validCount = 0;
        let assignedCount = 0;
        let unassignedCount = 0;
        for (const item of distinct) {
          if (idx.byKey.has(item.normalizedKey) || idx.byValue.has(item.normalizedKey)) {
            validCount += 1;
          } else if (assigned[item.normalizedKey]) {
            assignedCount += 1;
          } else {
            unassignedCount += 1;
          }
        }
        result[fieldId] = {
          total: distinct.length,
          valid: validCount,
          assigned: assignedCount,
          unassigned: unassignedCount,
        };
      }
      return result;
    },
    overallSummary() {
      const s = this.summaryByField;
      const out = { total: 0, valid: 0, assigned: 0, unassigned: 0, fields: 0 };
      for (const fid of Object.keys(s)) {
        out.fields += 1;
        out.total += s[fid].total;
        out.valid += s[fid].valid;
        out.assigned += s[fid].assigned;
        out.unassigned += s[fid].unassigned;
      }
      return out;
    },
  },
  actions: {
    setValueMap(fieldId, normalizedRawKey, target) {
      const inner = { ...(this.byField[fieldId] || {}) };
      if (target === null || target === undefined || target === '') {
        delete inner[normalizedRawKey];
      } else {
        inner[normalizedRawKey] = target;
      }
      this.byField = { ...this.byField, [fieldId]: inner };
    },
    clearField(fieldId) {
      const next = { ...this.byField };
      delete next[fieldId];
      this.byField = next;
    },
    proposeAuto(fieldId, options = {}) {
      const def = useDefinitionStore();
      const field = def.fields.find((f) => getFieldId(f) === fieldId);
      if (!field) return 0;
      const domain = getDomain(field) || [];
      const threshold = options.threshold ?? 0.7;
      const distinct = this.rawValuesByField[fieldId] || [];
      const inner = { ...(this.byField[fieldId] || {}) };
      const idx = buildDomainIndex(domain);
      let added = 0;
      for (const item of distinct) {
        if (idx.byKey.has(item.normalizedKey) || idx.byValue.has(item.normalizedKey)) continue;
        if (inner[item.normalizedKey]) continue;
        const match = findClosestDomain(item.sample, domain, { threshold });
        if (match) {
          inner[item.normalizedKey] = match.key != null ? String(match.key) : String(match.value);
          added += 1;
        }
      }
      this.byField = { ...this.byField, [fieldId]: inner };
      return added;
    },
    proposeAutoAll(options = {}) {
      let total = 0;
      for (const { fieldId } of this.eligibleFields) {
        total += this.proposeAuto(fieldId, options);
      }
      return total;
    },
    hydrate(state) {
      this.byField = state && typeof state === 'object' ? { ...state } : {};
    },
    reset() {
      this.byField = {};
    },
  },
});
